import "./styles.css";
import { useEffect, useMemo, useState } from "react";
import axios from "../../../../services/axios";
import { DeleteModalCreateProduct, SuccessModalCreateProduct } from "./Modal/Index";
import { useEditOnboarding } from "../../../../hooks/useEditOnboarding";
import { useFilter } from "../../../../hooks/useFilter";
import { OptionV2 } from "../../../FormControlsV2";
import { FooterEditOnboarding } from "../../../EditOnboarding/Footer";
import {
  createEmptyProductFormValue,
  ProductFormErrorsV2,
  ProductFormV2,
  ProductFormValueV2,
} from "../../../FormsV2";

function getSubClassificationLabel(classificationLabel?: string, labelMap?: Record<string, string>) {
  if (!classificationLabel) return "Sub classificação";
  return labelMap?.[classificationLabel] || "Sub classificação";
}

function getOptionLabel(value: any) {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return String(value.label || value.name || value.value || "");
}

function getOptionValue(value: any) {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return String(value.value || value.id || value.label || value.name || "");
}

function createProductFormFromDefault(defaultValue: any, classifications: OptionV2[]) {
  const defaultClassificationLabel = getOptionLabel(defaultValue?.classification);
  const defaultClassificationValue = getOptionValue(defaultValue?.classification_id || defaultValue?.classification);
  const classification =
    classifications.find((option) => String(option.value) === defaultClassificationValue) ||
    classifications.find((option) => option.label === defaultClassificationLabel) ||
    null;
  const subClassificationOptions = Array.isArray(classification?.info) ? (classification?.info as OptionV2[]) : [];
  const defaultSubClassificationLabel = getOptionLabel(defaultValue?.sub_classification);
  const defaultSubClassificationValue = getOptionValue(
    defaultValue?.sub_classification_id || defaultValue?.sub_classification
  );
  const subClassification =
    subClassificationOptions.find((option) => String(option.value) === defaultSubClassificationValue) ||
    subClassificationOptions.find((option) => option.label === defaultSubClassificationLabel) ||
    null;

  return {
    id: defaultValue?.id,
    trade_name: defaultValue?.trade_name || "",
    manufacturer: defaultValue?.manufacturer || "",
    classification,
    sub_classification: subClassification,
    expected_consumption: Number(defaultValue?.expected_consumption || 0),
    supply_frequency: Number(defaultValue?.supply_frequency || 0),
  } as ProductFormValueV2;
}

export function CreateProducts() {
  const { setOptionForm, defaultValue } = useEditOnboarding();
  const { setSideBarOptionSelected } = useFilter();
  const [productValue, setProductValue] = useState<ProductFormValueV2>(() => createEmptyProductFormValue());
  const [errors, setErrors] = useState<ProductFormErrorsV2>({});
  const [classifications, setClassifications] = useState<OptionV2[]>([]);
  const [classificationLabelMap, setClassificationLabelMap] = useState<Record<string, string>>({});
  const [modalDeleteVisible, setModalDeleteVisible] = useState(false);
  const [successCreator, setSuccessCreator] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const subClassificationLabel = useMemo(
    () => getSubClassificationLabel(productValue.classification?.label, classificationLabelMap),
    [classificationLabelMap, productValue.classification]
  );

  const formIsComplete = !!(
    productValue.trade_name.trim() &&
    productValue.manufacturer.trim() &&
    productValue.classification &&
    productValue.sub_classification &&
    productValue.expected_consumption > 0 &&
    productValue.supply_frequency > 0
  );

  function validate(value = productValue) {
    const validation: ProductFormErrorsV2 = {};

    if (!value.trade_name.trim()) validation.trade_name = "Nome do produto é necessário.";
    if (!value.manufacturer.trim()) validation.manufacturer = "Fabricante é necessário.";
    if (!value.classification) validation.classification = "Campo obrigatório não preenchido.";
    if (!value.sub_classification) validation.sub_classification = "Campo obrigatório não preenchido.";
    if (value.expected_consumption <= 0) validation.expected_consumption = "Preencha o consumo esperado.";
    if (value.supply_frequency <= 0) validation.supply_frequency = "Preencha a frequência de fornecimento.";

    setErrors(validation);
    return Object.keys(validation).length === 0;
  }

  function handleChange(nextValue: ProductFormValueV2) {
    setProductValue(nextValue);
    if (Object.keys(errors).length) validate(nextValue);
  }

  function submitProduct() {
    if (submitting || !validate()) return;

    const requestBody = {
      manufacturer: productValue.manufacturer,
      trade_name: productValue.trade_name,
      sub_classification_id: Number(productValue.sub_classification?.value),
      expected_consumption: productValue.expected_consumption,
      supply_frequency: productValue.supply_frequency,
    };

    setSubmitting(true);
    const request = defaultValue
      ? axios.patch("/products/supplement", { id: productValue.id, ...requestBody })
      : axios.post("/products/supplement", requestBody);

    request
      .then(() => setSuccessCreator(true))
      .finally(() => setSubmitting(false));
  }

  function deleteProduct(id: number) {
    axios.delete(`/products/${id}`).then(() => {
      setOptionForm(undefined);
      setModalDeleteVisible(false);
    });
  }

  useEffect(() => {
    axios.get("/products/supplement/classification").then(({ data }) => {
      setClassificationLabelMap({
        [data[0]?.name]: "Tipo de sal",
        [data[1]?.name]: "Classificação de consumo esperado",
        [data[2]?.name]: "Classificação de consumo esperado",
      });

      setClassifications(
        data.map((classification: any) => ({
          label: classification.name,
          value: classification.name,
          info: classification.sub_classification.map((subClassification: any) => ({
            label: subClassification.name,
            value: subClassification.id,
          })),
        }))
      );
    });
  }, []);

  useEffect(() => {
    if (!defaultValue || !classifications.length) return;
    setProductValue(createProductFormFromDefault(defaultValue, classifications));
  }, [classifications, defaultValue]);

  const summarySlot = (
    <aside className="create-product-v2__summary">
      <div className="body_resume">
        <div className="formContainerProduct">
          <label className="body_title">Nome comercial</label>
          <label className="container_formResult">{productValue.trade_name}</label>
        </div>
        <div className="formContainerProduct">
          <label className="body_title">Fabricante</label>
          <label className="container_formResult">{productValue.manufacturer}</label>
        </div>
        <div className="formContainerProduct">
          <label className="body_title">Consumo</label>
          <label className="container_formResult">
            {productValue.expected_consumption ? `${productValue.expected_consumption} g/kg de PV` : ""}
          </label>
        </div>
        <div className="formContainerProduct">
          <label className="body_title">Frequência</label>
          <label className="container_formResult last">
            {productValue.supply_frequency ? `${productValue.supply_frequency} x por semana` : ""}
          </label>
        </div>
      </div>
    </aside>
  );

  return (
    <>
      <main className="body__formEditOnboarding create-product-v2">
        <ProductFormV2
          value={productValue}
          errors={errors}
          classifications={classifications}
          subClassificationLabel={subClassificationLabel}
          sideSlot={summarySlot}
          disabled={submitting}
          onChange={handleChange}
        />
      </main>

      <FooterEditOnboarding
        undoFunction={() => setOptionForm(undefined)}
        saveFunction={submitProduct}
        deleteFunction={defaultValue?.can_delete ? () => setModalDeleteVisible(true) : undefined}
        isNew={!defaultValue}
        disabledSubmit={!formIsComplete || submitting}
      />

      <SuccessModalCreateProduct
        visible={successCreator}
        onClose={() => {
          setOptionForm(undefined);
          setSuccessCreator(false);
        }}
        backScreen={() => {
          setSideBarOptionSelected("MAPA");
          setOptionForm(undefined);
          setSuccessCreator(false);
        }}
        newProduct={() => {
          setSuccessCreator(false);
          setOptionForm(undefined);
          setTimeout(() => setOptionForm("create-products"), 1);
        }}
      />

      <DeleteModalCreateProduct
        visible={modalDeleteVisible}
        onClose={() => setModalDeleteVisible(false)}
        onConfirm={() => defaultValue?.id && deleteProduct(defaultValue.id)}
      />
    </>
  );
}

